import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  Card,
  DatePicker,
  Select,
  message,
} from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
const { RangePicker } = DatePicker;

export interface FilterValues {
  request_number?: string;
  status?: (string | number)[];
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  [key: string]: any;
}

interface Props {
  filters?: any[];
  statuses?: any[];
  templates?: any[];
  onFilter: (values: FilterValues) => void;
  onExport?: () => Promise<void> | void;
  showRequestNumber?: boolean;
  showDates?: boolean;
  showStatus?: boolean;
  showDescription?: boolean;
  showTemplate?: boolean;
}

const stripEmpty = (values: Record<string, any>): FilterValues => {
  const result: FilterValues = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    result[key] = value;
  });
  return result;
};

const DynamicFilterForm = ({
  filters = [],
  statuses = [],
  templates = [],
  onFilter,
  onExport,
  showRequestNumber = true,
  showDates = true,
  showStatus = true,
  showDescription = false,
  showTemplate = false,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!onExport) return;
    setExporting(true);
    try {
      await onExport();
    } catch (e) {
      message.error(t("Export failed. Please try again."));
    } finally {
      setExporting(false);
    }
  };

  const onFinish = (values: any) => {
    const normalized: Record<string, any> = { ...values };

    if (Array.isArray(values.start_date) && values.start_date.length === 2) {
      normalized.start_date_from = values.start_date[0]?.toISOString?.();
      normalized.start_date_to = values.start_date[1]?.toISOString?.();
    }
    delete normalized.start_date;

    if (Array.isArray(values.end_date) && values.end_date.length === 2) {
      normalized.end_date_from = values.end_date[0]?.toISOString?.();
      normalized.end_date_to = values.end_date[1]?.toISOString?.();
    }
    delete normalized.end_date;

    onFilter(stripEmpty(normalized));
  };

  const handleCleanFilters = () => {
    form.resetFields();
    onFilter({});
  };

  const hasStatuses = showStatus && statuses.length > 0;
  const hasTemplates = showTemplate && templates.length > 0;
  const hasDynamicFilters = filters.length > 0;

  return (
    <Card
      title={t("Filter Panel")}
      bordered={true}
      style={{ width: "100%", marginBottom: "2em" }}
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Row gutter={[16, 16]}>
          {showRequestNumber && (
            <Col key="request_number" xs={24} sm={12} md={6}>
              <Form.Item name="request_number" label={t("Request number")}>
                <Input placeholder={t("Request number")} allowClear />
              </Form.Item>
            </Col>
          )}
          {showDates && (
            <>
              <Col key="start_date" xs={24} sm={12} md={6}>
                <Form.Item name="start_date" label={t("Start date")}>
                  <RangePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col key="end_date" xs={24} sm={12} md={6}>
                <Form.Item name="end_date" label={t("End date")}>
                  <RangePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </>
          )}
          {hasStatuses && (
            <Col key="status" xs={24} sm={12} md={6}>
              <Form.Item name="status" label={t("Status")}>
                <Select
                  mode="multiple"
                  placeholder={t("Please select")}
                  allowClear
                >
                  {statuses.map((x: any) => (
                    <Select.Option key={x.id} value={x.id}>
                      {x.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {showDescription && (
            <Col key="description" xs={24} sm={12} md={6}>
              <Form.Item name="description" label={t("Description")}>
                <Input placeholder={t("Description")} allowClear />
              </Form.Item>
            </Col>
          )}
          {hasTemplates && (
            <Col key="template" xs={24} sm={12} md={6}>
              <Form.Item name="template" label={t("Template")}>
                <Select
                  mode="multiple"
                  placeholder={t("Please select")}
                  allowClear
                  optionFilterProp="children"
                  showSearch
                >
                  {templates.map((x: any) => (
                    <Select.Option key={x.id} value={x.id}>
                      {x.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
        {hasDynamicFilters && (
          <Row gutter={[16, 16]}>
            {filters.map((filter) => (
              <Col key={filter.field_name} xs={24} sm={12} md={8}>
                <Form.Item name={filter.field_name} label={filter.title}>
                  <Input placeholder={filter.title} allowClear />
                </Form.Item>
              </Col>
            ))}
          </Row>
        )}

        <Form.Item>
          <Space wrap>
            <Button type="primary" htmlType="submit">
              {t("Search")}
            </Button>
            <Button onClick={handleCleanFilters}>{t("Clean")}</Button>
            {onExport && (
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                {t("Export To Excel")}
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default DynamicFilterForm;
